/**
 * "This device" — what is on it, and how to get a copy off it (D-020).
 *
 * ── Why this is a screen and not a paragraph on the survey list ─────────────
 * Two audiences, one screen. The SURVEYOR comes here to make a backup or to
 * rescue work that is in the wrong container. The OFFICE comes here down a
 * telephone line — "open This device and read me the first line" — and that is
 * the whole of the diagnosis that took an hour of source-reading on 2026-08-06.
 * Neither of those belongs on the survey list, where the one obvious next action
 * is starting or opening a survey (D-015).
 *
 * The order is deliberate and is the order of the questions:
 *   what is on this phone, and which phone am I even looking at
 *   → how do I get a copy of it
 *   → how do I put one back.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { Card, Eyebrow } from '../components';
import { RestoreFromFile, SaveDeviceBackup } from '../components/SurveyTransfer';
import { hrefFor } from '../routing';
import { isStandalone } from '../state/displayMode';
import { useServices } from '../state/services';
import { storageDiagnostic } from '../state/storage';
import { APP_BUILD } from '../../core/build';
import type { StorageSnapshot } from '../../db/store';

export function DeviceScreen(): ReactNode {

  const { store, linked } = useServices();
  // Read once at mount: the display mode cannot change without a page load, so
  // re-reading it per render would only make the copy depend on render timing.
  const [standalone] = useState(isStandalone);
  const [snapshot, setSnapshot] = useState<StorageSnapshot | null>(null);

  useEffect(() => {
    let cancelled = false;
    const refresh = (): void => {
      void store
        .storageSnapshot()
        .then((next) => {
          if (!cancelled) setSnapshot(next);
        })
        .catch(() => undefined);
    };
    refresh();
    // Every committed write, so the counts are never one import out of date.
    const unsubscribe = store.subscribe(refresh);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [store]);

  const diagnostic =
    snapshot === null ? null : storageDiagnostic({ ...snapshot, standalone, linked });

  return (
    <>
      <Eyebrow>What is on this device</Eyebrow>

      <Card>
        {diagnostic === null ? (
          <p className="ruleline" data-legibility="helper">
            Reading this device…
          </p>
        ) : (
          <div data-testid="storage-diagnostic">
            <p className="q" data-legibility="question">
              {diagnostic.counts}
            </p>
            <p className="ruleline" data-legibility="helper">
              {diagnostic.where}
            </p>
            {/* The counts and the container are the diagnosis (D-020); the
              * explanations that used to follow them are gone (D-029 R1) —
              * the pill and the setup button already carry the unlinked state. */}
            <p className="ruleline" data-legibility="helper">
              {diagnostic.pictures}
            </p>
            {/* The build this app is running (round 8.1, task 1) — the fourth
              * fact the office asks for over the phone, right beside the
              * counts, the container and the pictures. */}
            <p className="ruleline" data-legibility="helper">
              Build {APP_BUILD}
            </p>
          </div>
        )}
        {!linked ? (
          <a className="btn btn--ghost" href={hrefFor({ name: 'access' })}>
            Set up this device
          </a>
        ) : null}
      </Card>

      <Eyebrow>Save a backup</Eyebrow>
      <SaveDeviceBackup />

      <Eyebrow>Bring surveys in from a file</Eyebrow>
      <RestoreFromFile />

      <div className="navbar">
        <a className="btn btn--ghost" href={hrefFor({ name: 'home' })}>
          My surveys
        </a>
      </div>
    </>
  );
}
